import {Injectable} from '@angular/core';
import {TreeNode} from 'primeng/api';
import {IJsonToTreeDataConverter} from '@ispm/shared/interfaces/json-tree-data-converter.interface';
import {get, has} from 'lodash';
import {NodeType} from "@ispm/shared/bpmn-io/node-type";

@Injectable()
export class PathwayJsonTreeConverterService implements IJsonToTreeDataConverter {

  public convertDataToTreeNodes(data: any, pathwayBpmnName: string, expanded: boolean = false): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    if (has(data, 'nodes')) {
      const bpmnNodes = get(data, 'nodes'); // main object

      const bpmnNodeId = 'head_node_id';
      const nodeName = pathwayBpmnName;
      const bpmnNode = get(bpmnNodes, bpmnNodeId);
      const newNode: TreeNode = this.createTreeNode(bpmnNode, nodeName, expanded);
      treeNodes.push(newNode);
      this.createChildNodes(bpmnNodes, bpmnNodeId, treeNodes, newNode, expanded);
    }
    return treeNodes;
  }

  private createTreeNode(descr: PropertyDescriptor, nodeName: string, expand: boolean = false): TreeNode {

    const key = this.getTreeNodeKey(descr['id'], descr['type']);
    const children: TreeNode[] = [];
    const nodeType: NodeType = descr['type'];
    const parentNodes = get(descr,'parentNodeIds',[]);

    // create treatment plan nodes
    if (has(descr, 'data.treatmentPlans')) {
      let nodeNameTp: string;
      const treatmentPlansArray = get(descr, 'data.treatmentPlans');

      treatmentPlansArray.forEach(tp => {
        nodeNameTp = get(tp, 'treatmentPlan.display', '').trim();

        children.push(
          {
            key: key + '_' + nodeNameTp,
            leaf: nodeType === NodeType.treatment,
            label: nodeNameTp,
            data: {id: descr['id'], type: nodeType, parent: ''},
            children: [],
            styleClass: 'p-leaf-selection',
            icon: "explorerview__icon__DLS_PatientMedicine_24",
            expanded: false
          });
      });
    }
    // create node
    return {
      key: key,
      leaf: nodeType === NodeType.treatment,
      label: !!nodeName ? nodeName : this.getNodeName(descr),
      data: { id: descr['id'], type: nodeType, parent: parentNodes.length > 0 ? parentNodes[0] : ''},
      children: children,
      icon: this.getNodeIcon(nodeType),
      styleClass: nodeType === NodeType.treatment ? 'p-leaf-selection' : '',
      expanded: nodeType === NodeType.treatment ? false : expand
    };
  }

  private getNodeName(descr: PropertyDescriptor) {
    let nodeName = has(descr, 'data.name') ? get(descr, 'data.name') : '';
    if (has(descr, 'data.treatmentPlans')) {
      nodeName = get(descr, 'data.treatmentPlans').length + ' On-pathway treatment plans';
    }
    return nodeName;
  }

  private getTreeNodeKey(id: string, type: number): string {
    return id + '_' + type;
  }

  // recursive function
  private createChildNodes(bpmnNodes, parentBpmnNodeId: string, treeNodes: TreeNode[], parentNode: TreeNode, expanded: boolean = false) {
    Object.getOwnPropertyNames(bpmnNodes).forEach(nodeKey => {
      const node = get(bpmnNodes, nodeKey);
      if (node['id'] !== parentBpmnNodeId && Array.isArray(node['parentNodeIds'])
        && node['parentNodeIds'].some(parentId => parentId === parentBpmnNodeId)) {

        if (node['type'] === NodeType.neighborhood_head) {
          this.createChildNodes(bpmnNodes, node['id'], treeNodes, parentNode, expanded);
        } else {
          const childNode = this.createTreeNode(node, get(node, 'data.name', ''), expanded);
          this.attachToParent(parentNode, childNode);
          this.createChildNodes(bpmnNodes, node['id'], treeNodes, childNode, expanded);
        }
      }
    });
  }

  private attachToParent(parentNode: TreeNode, childNode: TreeNode<any>) {
    if (!parentNode.children.some(child => child.label === childNode.label)) {
      parentNode.children.push(childNode);
    }
  }

  private getNodeIcon(nodeType: NodeType) {
    switch (nodeType) {
      case NodeType.head:
        return "pi pi-database";
      case NodeType.normal:
        return "";
      case NodeType.treatment:
        return "";
      case NodeType.neighborhood_entry:
        return "explorerview__icon__DLS_GoTo_24";
      case NodeType.neighborhood_head:
        return "pi pi-arrow-circle-left";
    }
    return "pi pi-database";
  }
}
